import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Quest, CreateQuestRequest } from '@ponzinomics/types';
import { featureApi, MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

const MOCK_QUESTS: Quest[] = [
  {
    id: '1',
    projectId: 'proj-1',
    title: 'Engage with Launch Tweet',
    description: 'Like, retweet, and comment on our launch announcement',
    type: 'twitter_like',
    points: 100,
    status: 'active',
    requirements: [
      {
        type: 'twitter_like',
        value: 'https://x.com/ponzinomics_io/status/1234567890',
        verified: false,
      },
    ],
    completionCount: 42,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '2',
    projectId: 'proj-1',
    title: 'Follow the Team',
    description: 'Follow our official accounts on X',
    type: 'twitter_follow',
    points: 50,
    status: 'active',
    requirements: [
      { type: 'twitter_follow', value: 'ponzinomics_io', verified: false },
      { type: 'twitter_follow', value: 'founder_handle', verified: false },
    ],
    completionCount: 128,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const questKeys = {
  all: ['quests'] as const,
  lists: () => [...questKeys.all, 'list'] as const,
  list: (projectId: string) => [...questKeys.lists(), projectId] as const,
  details: () => [...questKeys.all, 'detail'] as const,
  detail: (id: string) => [...questKeys.details(), id] as const,
};

type DashboardQuestMutationInput = Omit<CreateQuestRequest, 'type' | 'requirements'> & {
  type: Quest['type'];
  requirements: Array<{ type: string; value: string }>;
  status?: Quest['status'];
  category?: string;
  frequency?: 'daily' | 'weekly' | 'one_time' | 'streak';
  imageUrl?: string;
  sortOrder?: number;
};

type RawQuest = {
  id?: unknown;
  projectId?: unknown;
  title?: unknown;
  name?: unknown;
  description?: unknown;
  type?: unknown;
  points?: unknown;
  rewardPoints?: unknown;
  status?: unknown;
  requirements?: unknown;
  tasks?: unknown;
  completionCount?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  startsAt?: unknown;
  startAt?: unknown;
  endsAt?: unknown;
  endAt?: unknown;
  maxCompletions?: unknown;
};

const QUEST_TYPE_TO_API: Record<Quest['type'], string> = {
  twitter_follow: 'TWITTER_FOLLOW',
  // TWITTER_LIKE is not verifiable in the current backend provider, so map the
  // legacy "engagement" flow to a retweet-based quest that can be validated.
  twitter_like: 'TWITTER_RETWEET',
  twitter_retweet: 'TWITTER_RETWEET',
  twitter_reply: 'TWITTER_REPLY',
  wallet_connect: 'WALLET_CONNECT',
  token_hold: 'TOKEN_HOLD',
  custom: 'CUSTOM',
};

const QUEST_STATUS_TO_API: Record<Quest['status'], string> = {
  draft: 'DRAFT',
  active: 'ACTIVE',
  paused: 'PAUSED',
  completed: 'COMPLETED',
  expired: 'EXPIRED',
};

const QUEST_FREQUENCY_TO_API: Record<
  NonNullable<DashboardQuestMutationInput['frequency']>,
  string
> = {
  daily: 'DAILY',
  weekly: 'WEEKLY',
  one_time: 'ONE_TIME',
  streak: 'STREAK',
};

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function stripAtPrefix(value: string): string {
  return value.replace(/^@/, '').trim();
}

function extractTweetId(value: string): string {
  const match = value.match(/status\/(\d+)/);
  return match?.[1] ?? value.trim();
}

export function normalizeQuestType(rawType: unknown, requirements?: unknown): Quest['type'] {
  const type = asString(rawType);

  switch (type) {
    case 'twitter_follow':
    case 'twitter_like':
    case 'twitter_retweet':
    case 'twitter_reply':
    case 'wallet_connect':
    case 'token_hold':
    case 'custom':
      return type;
    case 'TWITTER_FOLLOW':
      return 'twitter_follow';
    case 'TWITTER_LIKE':
      return 'twitter_like';
    case 'TWITTER_RETWEET':
      return 'twitter_retweet';
    case 'TWITTER_REPLY':
      return 'twitter_reply';
    case 'WALLET_CONNECT':
      return 'wallet_connect';
    case 'TOKEN_HOLD':
      return 'token_hold';
    case 'CUSTOM':
      return 'custom';
    case 'SOCIAL': {
      const record =
        requirements && typeof requirements === 'object'
          ? (requirements as Record<string, unknown>)
          : null;
      if (record?.handle) return 'twitter_follow';
      if (record?.tweetId) return 'twitter_retweet';
      return 'custom';
    }
    default:
      return 'custom';
  }
}

export function normalizeQuestStatus(rawStatus: unknown): Quest['status'] {
  const status = asString(rawStatus);

  switch (status) {
    case 'draft':
    case 'active':
    case 'paused':
    case 'completed':
    case 'expired':
      return status;
    case 'DRAFT':
      return 'draft';
    case 'ACTIVE':
      return 'active';
    case 'PAUSED':
      return 'paused';
    case 'COMPLETED':
      return 'completed';
    case 'ARCHIVED':
    case 'EXPIRED':
      return 'expired';
    default:
      return 'draft';
  }
}

export function normalizeQuestRequirements(
  rawRequirements: unknown,
  type: Quest['type']
): Quest['requirements'] {
  if (Array.isArray(rawRequirements)) {
    return rawRequirements
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const record = item as Record<string, unknown>;
        const value = asString(record.value);
        if (!value) return null;
        return {
          type: asString(record.type) ?? type,
          value,
          verified: Boolean(record.verified),
        };
      })
      .filter((item): item is Quest['requirements'][number] => item !== null);
  }

  if (!rawRequirements || typeof rawRequirements !== 'object') {
    return [];
  }

  const record = rawRequirements as Record<string, unknown>;

  switch (type) {
    case 'twitter_follow': {
      const handle = asString(record.handle) ?? asString(record.value);
      return handle
        ? [{ type: 'twitter_follow', value: stripAtPrefix(handle), verified: false }]
        : [];
    }
    case 'twitter_like':
    case 'twitter_retweet':
    case 'twitter_reply': {
      const tweetId =
        asString(record.tweetId) ??
        asString(record.tweetUrl) ??
        asString(record.url) ??
        asString(record.value);
      return tweetId ? [{ type, value: tweetId, verified: false }] : [];
    }
    case 'wallet_connect':
    case 'token_hold':
    case 'custom': {
      const value = asString(record.value);
      return value ? [{ type, value, verified: false }] : [];
    }
    default:
      return [];
  }
}

export function normalizeQuest(rawQuest: RawQuest): Quest {
  const requirementsSource = rawQuest.requirements ?? rawQuest.tasks;
  const type = normalizeQuestType(rawQuest.type, requirementsSource);
  const quest: Quest = {
    id: asString(rawQuest.id) ?? '',
    projectId: asString(rawQuest.projectId) ?? '',
    title: asString(rawQuest.title) ?? asString(rawQuest.name) ?? 'Untitled Quest',
    description: asString(rawQuest.description) ?? '',
    type,
    points: asNumber(rawQuest.points) ?? asNumber(rawQuest.rewardPoints) ?? 0,
    status: normalizeQuestStatus(rawQuest.status),
    requirements: normalizeQuestRequirements(requirementsSource, type),
    completionCount: asNumber(rawQuest.completionCount) ?? 0,
    createdAt: asString(rawQuest.createdAt) ?? new Date().toISOString(),
    updatedAt: asString(rawQuest.updatedAt) ?? new Date().toISOString(),
  };

  const startsAt = asString(rawQuest.startsAt) ?? asString(rawQuest.startAt);
  const endsAt = asString(rawQuest.endsAt) ?? asString(rawQuest.endAt);
  const maxCompletions = asNumber(rawQuest.maxCompletions);

  if (startsAt !== undefined) quest.startsAt = startsAt;
  if (endsAt !== undefined) quest.endsAt = endsAt;
  if (maxCompletions !== undefined) quest.maxCompletions = maxCompletions;

  return quest;
}

function inferQuestCategory(type: Quest['type']): string {
  switch (type) {
    case 'twitter_follow':
    case 'twitter_like':
    case 'twitter_retweet':
    case 'twitter_reply':
      return 'X';
    default:
      return 'ECOSYSTEM';
  }
}

function serializeQuestCategory(category: string | undefined, type: Quest['type']): string {
  const normalized = category?.trim().toUpperCase();
  if (normalized) {
    return normalized;
  }
  return inferQuestCategory(type);
}

function serializeQuestRequirements(input: DashboardQuestMutationInput): Record<string, unknown> {
  const firstRequirement = input.requirements[0]?.value?.trim();

  switch (input.type) {
    case 'twitter_follow':
      return {
        handle: firstRequirement ? stripAtPrefix(firstRequirement) : '',
      };
    case 'twitter_like':
    case 'twitter_retweet':
    case 'twitter_reply':
      return {
        tweetId: firstRequirement ? extractTweetId(firstRequirement) : '',
      };
    case 'wallet_connect':
      return {
        wallet: firstRequirement ?? '',
      };
    case 'token_hold':
      return {
        token: firstRequirement ?? '',
      };
    case 'custom':
      return {
        value: firstRequirement ?? '',
      };
    default:
      return {};
  }
}

export function serializeQuestCreateRequest(
  input: DashboardQuestMutationInput
): Record<string, unknown> {
  return {
    title: input.title,
    description: input.description,
    type: QUEST_TYPE_TO_API[input.type],
    points: input.points,
    status: input.status ? QUEST_STATUS_TO_API[input.status] : undefined,
    requirements: serializeQuestRequirements(input),
    category: serializeQuestCategory(input.category, input.type),
    frequency: input.frequency ? QUEST_FREQUENCY_TO_API[input.frequency] : 'ONE_TIME',
    imageUrl: input.imageUrl,
    sortOrder: input.sortOrder,
    maxCompletions: input.maxCompletions,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
  };
}

export function serializeQuestUpdateRequest(
  input: Partial<DashboardQuestMutationInput>
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if (input.title !== undefined) payload.title = input.title;
  if (input.description !== undefined) payload.description = input.description;
  if (input.type !== undefined) payload.type = QUEST_TYPE_TO_API[input.type];
  if (input.points !== undefined) payload.points = input.points;
  if (input.status !== undefined) payload.status = QUEST_STATUS_TO_API[input.status];
  if (input.requirements !== undefined && input.type !== undefined) {
    payload.requirements = serializeQuestRequirements(input as DashboardQuestMutationInput);
  }
  if (input.category !== undefined || input.type !== undefined) {
    payload.category = serializeQuestCategory(input.category, input.type ?? 'custom');
  }
  if (input.frequency !== undefined) payload.frequency = QUEST_FREQUENCY_TO_API[input.frequency];
  if (input.imageUrl !== undefined) payload.imageUrl = input.imageUrl;
  if (input.sortOrder !== undefined) payload.sortOrder = input.sortOrder;
  if (input.maxCompletions !== undefined) payload.maxCompletions = input.maxCompletions;
  if (input.startsAt !== undefined) payload.startsAt = input.startsAt;
  if (input.endsAt !== undefined) payload.endsAt = input.endsAt;

  return payload;
}

export function useQuests(): ReturnType<typeof useQuery<Quest[]>> {
  const { projectId } = useCurrentProject();

  return useQuery({
    queryKey: questKeys.list(projectId ?? ''),
    queryFn: async () => {
      if (MOCK_MODE) return MOCK_QUESTS;
      const rawQuests = await featureApi.get<RawQuest[]>(`/quests?projectId=${projectId}`);
      return rawQuests.map(normalizeQuest);
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateQuest() {
  const queryClient = useQueryClient();
  const { projectId } = useCurrentProject();

  return useMutation<Quest, Error, DashboardQuestMutationInput>({
    mutationFn: async (data: DashboardQuestMutationInput) => {
      if (MOCK_MODE) {
        const mock: Quest = {
          id: `quest-${Date.now()}`,
          projectId: projectId ?? '',
          title: data.title,
          description: data.description,
          type: data.type as Quest['type'],
          points: data.points,
          status: data.status ?? 'draft',
          requirements: data.requirements.map((r) => ({ ...r, verified: false })),
          completionCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        if (data.startsAt) mock.startsAt = data.startsAt;
        if (data.endsAt) mock.endsAt = data.endsAt;
        if (data.maxCompletions) mock.maxCompletions = data.maxCompletions;
        return mock;
      }
      const rawQuest = await featureApi.post<RawQuest>('/quests', {
        ...serializeQuestCreateRequest(data),
        projectId,
      });
      return normalizeQuest(rawQuest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questKeys.lists() });
    },
  });
}

export function useUpdateQuest(): ReturnType<
  typeof useMutation<Quest, Error, { id: string; data: Partial<DashboardQuestMutationInput> }>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<DashboardQuestMutationInput>;
    }) => {
      if (MOCK_MODE) {
        const base = MOCK_QUESTS.find((q) => q.id === id) ?? MOCK_QUESTS[0]!;
        return { ...base, ...data, updatedAt: new Date().toISOString() } as Quest;
      }
      const rawQuest = await featureApi.patch<RawQuest>(
        `/quests/${id}`,
        serializeQuestUpdateRequest(data)
      );
      return normalizeQuest(rawQuest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questKeys.lists() });
    },
  });
}

export function useDeleteQuest(): ReturnType<typeof useMutation<string, Error, string>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (MOCK_MODE) return id;
      await featureApi.delete(`/quests/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questKeys.lists() });
    },
  });
}
